<template>
	<section id="letaky-a-katalogy" class="overflow-hidden" :aria-label="sectionName">
		<div
			class="py-5 md:py-12 text-body-secondary"
			:class="{'bg-mujglobus-green-light': lightBg}"
		>
			<div v-if="selectedBranchLabelFresh" class="container">
				<div class="block md:flex justify-between items-center gap-x-4 mb-6 md:mb-10">
					<h2 class="typo-h1 text-heading-primary mb-2">
						Akční letáky a katalogy
					</h2>
				</div>

				<div class="bg-orange-100 max-w-screen-md mx-auto p-4 sm:py-10 sm:px-12 rounded-2xl text-center">
					<NuxtIcon name="custom/paper-open" class="text-[2.75rem] text-icon-primary flex justify-center mb-4" filled />

					<p v-if="selectedParentHypermarketInstance" class="text-h4-sm">
						Vybraný sortiment z&nbsp;letáku pro hypermarket <strong class="font-bold">{{ selectedParentHypermarketInstance.content.name }}</strong> se vyskytuje i&nbsp;na pobočce Fresh.
					</p>

					<div class="flex justify-center items-center flex-wrap gap-4 sm:gap-2 mt-6">
						<AbsoluteInternalLink
							:to="branchSpecificPath('/hypermarket/akcni-nabidka/')"
							class="btn btn-primary btn-lg max-sm:w-full justify-center"
						>
							Akční nabídka Fresh
						</AbsoluteInternalLink>
						<AbsoluteInternalLink
							v-if="selectedParentHypermarketInstance"
							:to="branchSpecificPath({name: 'branch-letaky', params: {branch: selectedParentHypermarketInstance.slug}})"
							class="btn btn-secondary btn-lg shadow-sm sm:whitespace-nowrap max-sm:w-full justify-center"
						>
							<NuxtIcon name="custom/arrow-right" class="text-lg" filled />
							Zobrazit leták
						</AbsoluteInternalLink>
					</div>
				</div>
			</div>
			<div v-else class="container">
				<div class="block md:flex justify-between items-center gap-x-4 mb-6 md:mb-10">
					<div class="max-w-[32.5rem]">
						<h2 class="typo-h1 text-heading-primary mb-2">
							Akční letáky a katalogy
						</h2>
						<p class="text-body-secondary">
							Ceny na různých hypermarketech se mohou v letácích lišit vyberte si prosím svoji pobočku.
						</p>
					</div>
					<div class="flex flex-col md:items-end gap-3 max-md:mt-2">
						<div class="flex items-center gap-2 font-bold order-2 md:order-1">
							<NuxtIcon name="bell" class="text-xl" filled />
							<span class="typo-body-m">
								Každou středu nový leták
							</span>
						</div>
						<InformationAboutHypermarket
							text="Informace o letácích se vztahují k"
							class="order-1 md:order-2"
							:default-hm="DEFAULT_HYPERMARKET"
						/>
					</div>
				</div>

				<div v-if="status === 'pending'" class="text-center py-5">
					<NuxtIcon
						name="custom/loader"
						class="text-icon-primary p-2 animate-spin text-2xl md:text-3xl lg:text-4xl inline-block"
						fill
					/>
				</div>
				<template v-else-if="leaflets?.actionOffersDocuments?.length">
					<CarouselSkeleton
						:items="leaflets?.actionOffersDocuments"
						list-class="inline-flex w-full min-w-max gap-2 sm:gap-6 lg:min-w-full lg:w-fit lg:grid lg:grid-cols-5"
						page-per-item
						snap-items
						center-item-snap
					>
						<template #item="{item: leaflet, index}">
							<li :key="index" class="rem:w-[150px] sm:rem:w-[230px] lg:w-full">
								<LeafletTile
									:section="sectionName"
									:counter="computerToHumanIndexing(index)"
									:leaflet="leaflet"
									:current-main-leaflet-id="currentMainLeafletId"
									light-bg
								/>
							</li>
						</template>
					</CarouselSkeleton>
					<div class="flex justify-center mt-10">
						<AbsoluteInternalLink
							:to="branchSpecificPath({name: 'letaky'})"
							class="btn btn-secondary btn-lg"
						>
							<NuxtIcon
								name="custom/arrow-right"
								class="text-lg transition duration-300 max-xl:hidden"
								filled
								aria-hidden="true"
							/>
							Všechny letáky {{ selectedHypermarketWithFallbackInstance?.content.name }}
						</AbsoluteInternalLink>
					</div>
				</template>
				<div v-else class="text-center my-16">
					<NuxtIcon name="frown" filled class="text-7xl mb-4 text-icon-primary text-center inline-block" />
					<h3 class="typo-h4 text-heading-primary mb-6">
						Bohužel jsme nenašli žádné letáky
					</h3>
				</div>
			</div>
		</div>
	</section>
</template>

<script setup lang="ts">
	import {storeToRefs} from 'pinia';
	import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
	import {GsoaActionOffersService} from '~/openapi';
	import {DEFAULT_HYPERMARKET} from '#shared/constants';

	interface Props {
		lightBg?: boolean
	}

	defineProps<Props>();

	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {
		selectedHypermarket,
		selectedHypermarketWithFallbackInstance,
		hypermarketsBySlug,
		selectedHypermarketInstance,
		selectedParentHypermarketInstance,
	} = storeToRefs(hypermarketsStore);
	const selectedBranchLabelFresh = computed(() => selectedHypermarketInstance.value?.branchType === 'fresh');
	const houseNumber = computed(() => selectedHypermarketWithFallbackInstance.value?.content.gsoaId);
	const sectionName = computed(() => `Akční nabídka ${selectedHypermarket.value ? hypermarketsBySlug.value[selectedHypermarket.value]?.content.name : ''}`);
	const branchSpecificPath = useBranchSpecificPath();

	const {data: leaflets, status} = await useAsyncData(
		`leaflets-section-listing-${houseNumber.value ?? 'globus'}`,
		() => cancelTimeout(
			() => houseNumber.value
				? GsoaActionOffersService.getActionOffersDocuments({
					houseNumber: Number(houseNumber.value),
					orderingRule: '1',
					hideInComplete: true,
					...getLeafletsFilter(),
				})
				: Promise.resolve(null),
		),
		{watch: [houseNumber]},
	);

	const currentMainLeafletId = computed(() => getCurrentMainLeaflet(leaflets.value?.actionOffersDocuments ?? [])?.actionOfferId ?? null);
</script>
